import { useEffect } from 'react'
import { matchPath, useLocation } from 'react-router-dom'

import {
  EMBEDDED_CHAT_KEY,
  EMBEDDED_CHAT_VALUE,
} from '~/features/chatbot/constants'
import { EClientPath } from '~/models/api'

const GTM_DATA_ATTR = 'data-app-gtm'

const DATA_LAYER_KEY = 'dataLayer' as const

type WindowWithDataLayer = Window & { [DATA_LAYER_KEY]?: unknown[] }

function noopDataLayerPush(): number {
  return 0
}

let savedDataLayerPush: ((...args: unknown[]) => unknown) | null = null

const getContainerId = () =>
  import.meta.env.VITE_GTM_CONTAINER_ID as string | undefined

const shouldExcludeGtm = (pathname: string, search: string) => {
  const params = new URLSearchParams(search)

  if (params.get(EMBEDDED_CHAT_KEY) === EMBEDDED_CHAT_VALUE) {
    return true
  }

  return matchPath({ path: EClientPath.chat, end: true }, pathname) != null
}

const getDataLayer = (): unknown[] => {
  const w = window as WindowWithDataLayer
  if (!w[DATA_LAYER_KEY]) {
    w[DATA_LAYER_KEY] = []
  }
  return w[DATA_LAYER_KEY]!
}

const neutralizeDataLayer = () => {
  const dl = getDataLayer()
  const { push } = dl
  if (push !== noopDataLayerPush) {
    savedDataLayerPush = push.bind(dl) as (...args: unknown[]) => unknown
  }
  dl.push = noopDataLayerPush as typeof dl.push
}

const restoreDataLayer = () => {
  const w = window as WindowWithDataLayer
  const dl = w[DATA_LAYER_KEY]
  if (!dl) {
    return
  }
  if (savedDataLayerPush) {
    dl.push = savedDataLayerPush as typeof dl.push
    savedDataLayerPush = null
  }
}

const removeGtm = (containerId: string) => {
  const idQuery = `id=${encodeURIComponent(containerId)}`
  document.querySelectorAll('script').forEach((el) => {
    const src = el.getAttribute('src')
    if (src?.includes('googletagmanager.com/gtm.js') && src.includes(idQuery)) {
      el.remove()
    }
  })
  document
    .querySelectorAll(`script[${GTM_DATA_ATTR}]`)
    .forEach((el) => el.remove())
}

const injectGtm = (containerId: string) => {
  if (document.querySelector(`script[${GTM_DATA_ATTR}]`)) {
    return
  }

  const inline = document.createElement('script')
  inline.setAttribute(GTM_DATA_ATTR, '')
  inline.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${containerId}');`
  document.head.appendChild(inline)
}

const useGoogleTagManager = () => {
  const { pathname, search } = useLocation()

  useEffect(() => {
    const containerId = getContainerId()
    if (!containerId?.trim()) {
      return
    }

    const exclude = shouldExcludeGtm(pathname, search)

    if (exclude) {
      neutralizeDataLayer()
      removeGtm(containerId)
      return
    }

    restoreDataLayer()
    injectGtm(containerId)
  }, [pathname, search])
}

export default useGoogleTagManager
